<script setup lang="ts">
// Tournaments LIST (round-10, 2026-08-31): this screen is now just /tournaments — every
// tournament ever, newest first, with a status chip (Upcoming = registration, In progress,
// Finished + winner) — plus the create form. Clicking a row opens the detail screen
// (TournamentDetail.vue, #tournamentDetail?tournamentId=N), which owns registration, the
// all-v-all phase, and the bracket explorer. Round-11: multiple tournaments can be open at
// once — creating a new one just switches focus; badge scans register into the tournament
// whose DETAIL screen is open (the list screen falls back to the newest open one).
import { computed, onMounted, ref } from 'vue'
import { createTournament, getTournaments, ApiRequestError } from '../backend'
import { navigate } from '../store/nav'
import { refreshActiveTournament } from '../store/tournament'
import type { MatchTypeCode, Tournament, TournamentFormat } from '../types'
import OnScreenKeyboard from '../components/OnScreenKeyboard.vue'

const items = ref<Tournament[]>([])
const loadError = ref(false)
const loading = ref(false)

const createOpen = ref(false)
const nameInput = ref('')
const prizeInput = ref('')
const formatInput = ref<TournamentFormat>('rr_knockout')
// Round-13: the tournament dictates the format its matches are played in.
const matchTypeInput = ref<MatchTypeCode>('rapid11')
const bestOfInput = ref(3)
const keyboardTarget = ref<'name' | 'prize' | null>(null)
const creating = ref(false)
const createError = ref<string | null>(null)

const FORMATS: { value: TournamentFormat; label: string; hint: string }[] = [
  { value: 'rr_knockout', label: 'All-v-all + finals', hint: 'Everyone plays everyone, top players advance to a knockout' },
  { value: 'round_robin', label: 'All-v-all', hint: 'Everyone plays everyone — best record wins' },
  { value: 'single_elim', label: 'Knockout', hint: 'Elo-seeded single elimination bracket' },
]

async function load() {
  loading.value = true
  try {
    const res = await getTournaments()
    items.value = res.items
    loadError.value = false
  } catch {
    loadError.value = true
  } finally {
    loading.value = false
  }
}

onMounted(load)

function statusChip(t: Tournament): { label: string; cls: string } {
  if (t.status === 'registration') return { label: 'Upcoming', cls: 'bg-sky-900/60 text-sky-300' }
  if (t.status === 'in_progress') return { label: 'In progress', cls: 'bg-amber-900/60 text-amber-300' }
  return { label: 'Finished', cls: 'bg-neutral-800 text-neutral-400' }
}

function formatLabel(t: Tournament): string {
  return FORMATS.find((f) => f.value === t.format)?.label ?? t.format
}

function dateLabel(t: Tournament): string {
  return new Date(t.finishedAt ?? t.startedAt ?? t.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
}

function open(t: Tournament) {
  navigate('tournamentDetail', { tournamentId: t.id })
}

const keyboardModel = computed({
  get: () => (keyboardTarget.value === 'prize' ? prizeInput.value : nameInput.value),
  set: (v: string) => {
    if (keyboardTarget.value === 'prize') prizeInput.value = v
    else nameInput.value = v
  },
})

async function onCreate() {
  const name = nameInput.value.trim()
  if (!name || creating.value) return
  creating.value = true
  createError.value = null
  try {
    const t = await createTournament(name, formatInput.value, prizeInput.value.trim() || null, matchTypeInput.value, bestOfInput.value)
    nameInput.value = ''
    prizeInput.value = ''
    createOpen.value = false
    await refreshActiveTournament()
    navigate('tournamentDetail', { tournamentId: t.id })
  } catch (e) {
    createError.value = e instanceof ApiRequestError ? e.message : 'Could not create tournament'
  } finally {
    creating.value = false
  }
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col p-4 gap-3">
    <div class="flex items-center gap-3">
      <h1 class="text-xl font-bold flex-1">Tournaments</h1>
      <button
        type="button"
        class="tap-target px-4 rounded-xl bg-emerald-600 active:bg-emerald-700 font-semibold"
        @click="createOpen = true"
      >
        New tournament
      </button>
    </div>
    <div v-if="loadError" class="flex-1 flex items-center justify-center text-neutral-500">Unavailable</div>
    <div v-else class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2">
      <div v-if="!loading && items.length === 0" class="text-neutral-500">No tournaments yet — create the first one.</div>
      <button
        v-for="t in items"
        :key="t.id"
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-900 border border-neutral-800 px-4 py-3 text-left active:bg-neutral-800"
        @click="open(t)"
      >
        <div class="min-w-0 flex-1 flex flex-col">
          <span class="font-bold truncate">{{ t.name }}</span>
          <span class="text-[11px] font-mono text-neutral-500">{{ formatLabel(t) }} · {{ dateLabel(t) }}<template v-if="t.prize"> · 🏆 {{ t.prize }}</template></span>
        </div>
        <span class="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full" :class="statusChip(t).cls">
          {{ statusChip(t).label }}
        </span>
      </button>
    </div>

    <!-- Create dialog -->
    <div v-if="createOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" @click.self="createOpen = false">
      <div class="w-[520px] max-w-[94%] rounded-2xl bg-neutral-900 border border-neutral-700 p-6 flex flex-col gap-4 shadow-2xl">
        <h2 class="text-xl font-bold">New tournament</h2>
        <input
          v-model="nameInput"
          type="text"
          placeholder="Name…"
          class="tap-target rounded-xl bg-neutral-950 border border-neutral-800 px-4 text-base outline-none focus:ring-2 focus:ring-emerald-500"
          @focus="keyboardTarget = 'name'"
        />
        <input
          v-model="prizeInput"
          type="text"
          placeholder="Prize (optional) — e.g. a round of vermuts"
          class="tap-target rounded-xl bg-neutral-950 border border-neutral-800 px-4 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
          @focus="keyboardTarget = 'prize'"
        />
        <div class="flex flex-col gap-2">
          <button
            v-for="f in FORMATS"
            :key="f.value"
            type="button"
            class="tap-target w-full flex items-center gap-3 rounded-xl border px-4 text-left"
            :class="formatInput === f.value ? 'border-emerald-500 bg-emerald-950/40' : 'border-neutral-800 bg-neutral-950 active:bg-neutral-800'"
            @click="formatInput = f.value"
          >
            <span class="flex-1">
              <span class="block font-semibold text-sm">{{ f.label }}</span>
              <span class="block text-xs text-neutral-500">{{ f.hint }}</span>
            </span>
            <span class="w-4 h-4 rounded-full border-2 shrink-0" :class="formatInput === f.value ? 'border-emerald-400 bg-emerald-400' : 'border-neutral-600'" />
          </button>
        </div>
        <!-- Round-13: match format the tournament's games are played in (New match hides its
             own format rows for tournament matches — the server enforces these). -->
        <div class="flex items-center gap-3">
          <span class="text-[11px] font-bold text-neutral-400 uppercase tracking-wide w-20 shrink-0">Played as</span>
          <div class="flex gap-2">
            <button
              v-for="mt in [{ code: 'rapid11', label: 'Rapid 11' }, { code: 'classic21', label: 'Classic 21' }] as const"
              :key="mt.code"
              type="button"
              class="h-10 min-h-0 px-3 rounded-xl font-semibold text-xs"
              :class="matchTypeInput === mt.code ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
              @click="matchTypeInput = mt.code"
            >
              {{ mt.label }}
            </button>
          </div>
          <div class="flex gap-2 ml-auto">
            <button
              v-for="bo in [1, 3, 5]"
              :key="bo"
              type="button"
              class="h-10 min-h-0 w-10 min-w-0 rounded-xl font-semibold text-xs"
              :class="bestOfInput === bo ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
              @click="bestOfInput = bo"
            >
              {{ bo }}
            </button>
          </div>
        </div>
        <p v-if="createError" class="text-sm text-red-400">{{ createError }}</p>
        <div class="flex justify-end gap-2">
          <button type="button" class="tap-target px-5 rounded-xl bg-neutral-700 active:bg-neutral-600 font-semibold" @click="createOpen = false">Cancel</button>
          <button
            type="button"
            class="tap-target px-5 rounded-xl bg-emerald-600 active:bg-emerald-700 font-semibold disabled:opacity-40"
            :disabled="!nameInput.trim() || creating"
            @click="onCreate"
          >
            {{ creating ? 'Creating…' : 'Create' }}
          </button>
        </div>
        <OnScreenKeyboard v-if="keyboardTarget" v-model="keyboardModel" @done="keyboardTarget = null" />
      </div>
    </div>
  </div>
</template>
